import { coercePref } from './coerce-pref.util';
import { PINNED_OFF_MIRROR } from './attribute-preset-store.base';

/**
 * The user's stored text-size choice.
 *
 * NO 'system' MEDIA QUERY, AND YET 'system' IS MEANINGFUL HERE — which is the opposite of
 * `display.cvd`, where the absence of an OS signal meant the value had to go. The browser's own
 * default font size IS the system signal, and it is honoured automatically: the scale below is
 * expressed as a PERCENTAGE, so `100%` is whatever the user set in their browser and the pinned
 * steps multiply it. A user who raised their browser default to 20px and then picks 'large' gets
 * 22.5px, not 18px.
 *
 * THAT IS WHY THE RULE MAY NEVER BE WRITTEN IN px. `html { font-size: 16px }` is the single most
 * common way an app silently overrides a browser-level accessibility setting: it looks like a
 * reset, and it takes the preference away from every user who had already expressed one.
 *
 * Deliberately no 'small'. Shrinking text is not an accessibility affordance, and the one user who
 * wants it is served by browser zoom, which scales layout with it.
 */
export type FontScalePreset = 'system' | 'large' | 'larger';

/**
 * What is actually stamped. A separate, NULLABLE type for the same reason ResolvedContrast is one:
 * the default size is the attribute being ABSENT, so there is no 'system' attribute value and
 * writing one would look correct and scale nothing forever.
 */
export type ResolvedFontScale = 'large' | 'larger' | null;

/** Key in the /v2/userSettings blob. Frontend-owned and opaque to the backend. */
export const FONT_SCALE_KEY = 'display.fontSize';

/** localStorage key holding the RESOLVED scale, read by the inline script in index.html.
 *  A step, or absent for the default. (The PINNED_OFF_MIRROR sentinel is accepted on read but is
 *  never written for this preset: its only null is 'system', which is the default.) */
export const FONT_SCALE_STORAGE_KEY = 'g15.fontScale';

export const FONT_SCALE_PRESETS: readonly FontScalePreset[] = [
  'system',
  'large',
  'larger',
] as const;

/**
 * The percentages the stylesheet applies, transcribed here so a spec can hold the two to each
 * other.
 *
 * THE RANGE IS CONSERVATIVE, AND THE REASON FIRST GIVEN HERE WAS A COUNT RATHER THAN A
 * MEASUREMENT. It read: "this app carries roughly 1,140 px LAYOUT declarations ... and 81 px-keyed
 * media queries, none of which move when text does. Text therefore grows inside boxes that do not,
 * so the ceiling is set where that is an inconvenience rather than a clipped interface." Two things
 * were wrong with it. The figures were stale -- those same properties measure ~1,836 today, out of
 * 2,929 px declarations in `src/**\/*.scss`, with 84 media queries -- and, more to the point,
 * counting declarations is not measuring what a browser composites. Whether 125% clipped anything
 * was never checked.
 *
 * IT IS MEASURED NOW, by `e2e/font-scale-clipping.spec.ts`: on the three screens the hermetic suite
 * can reach, no text is clipped and the page never scrolls sideways at either step -- nor at 150%.
 * (Spill and sibling overlap are NOT checked by that gate.) The first loss is at 175%, and every
 * loss is a box of fixed size around text that grows -- table cells, a form field's height --
 * rather than the padding and gap the count blamed: a flex layout that wraps absorbs larger text, a
 * fixed box does not. One such box DID clip at 125% (a 320px grid column on Preferences) until the
 * probe learnt to see it; it is in rem now. That headroom is pinned per screen in
 * `e2e/font-scale-clipping-accepted.json`, in both directions, so a layout change that eats it
 * fails a gate instead of quietly removing the option.
 *
 * SO WHY IS IT STILL 125%? Because three screens are not the app, and those ~1,836 declarations
 * live mostly where that suite cannot reach. Raising a global preference on this sample would be
 * the failure this codebase keeps recording -- a figure measured on one sample and asserted of the
 * whole. The next step toward a wider ceiling is more screens under that same probe, not a bigger
 * number here; `font-scale.util.spec.ts` pins the bound so raising it stays a deliberate act.
 */
export const FONT_SCALE_PERCENT: Readonly<Record<Exclude<ResolvedFontScale, null>, number>> = {
  large: 112.5,
  larger: 125,
};

/** Coerce an untrusted value (a settings-blob entry or a cross-tab broadcast payload) to a known
 *  preset, through the shared prototype-key hardening every other display.* preference uses. */
export function coerceFontScale(v: unknown): FontScalePreset {
  return coercePref(v, FONT_SCALE_PRESETS, 'system');
}

/**
 * Map a stored preset to the scale actually stamped.
 *
 * Takes no second argument, unlike resolveContrast/resolveTheme: there is no OS signal to fold in,
 * because the browser applies its own default font size beneath this percentage rather than
 * reporting it through a media query. Kept as a function anyway so the mapping has one home and a
 * spec can drive it.
 */
export function resolveFontScale(preset: FontScalePreset): ResolvedFontScale {
  return preset === 'system' ? null : preset;
}

/**
 * Read a boot mirror back into the scale it records. THE ONE STATEMENT OF THE WHITELIST on the
 * TypeScript side, derived from FONT_SCALE_PERCENT's keys rather than spelled out, so a step added
 * to the stylesheet transcription is accepted here without a second edit.
 *
 * Three answers, as for every nullable preset: a known step is stamped; the PINNED_OFF_MIRROR
 * sentinel is a REAL answer of `null` ("the user pinned the default size"); anything else is
 * `undefined`, a miss. The inline script in index.html is a hand-written copy of the same rule
 * that cannot import this, so `src/index.boot-theme.spec.ts` RUNS that script against this
 * function for every step and for the sentinel -- a step accepted here and not there is a
 * preference that only applies once the bundle has parsed, which is the flash the mirror exists
 * to prevent.
 */
export function fontScaleFromMirror(mirror: string): ResolvedFontScale | undefined {
  if (Object.prototype.hasOwnProperty.call(FONT_SCALE_PERCENT, mirror)) {
    return mirror as Exclude<ResolvedFontScale, null>;
  }
  if (mirror === PINNED_OFF_MIRROR) return null;
  return undefined;
}
